import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Input, Modal, ModalBody, ModalHeader, Spinner } from "reactstrap";

const EMPTY = [];
const normalize = (key) => String(key).trim().toLowerCase();

export function instructionText(value) {
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (parsed && typeof parsed === "object") return instructionText(parsed);
    } catch (_error) { /* Plain text instructions are also supported. */ }
    return value;
  }
  if (value == null) return "";
  if (Array.isArray(value)) return value.map(instructionText).filter(Boolean).join("\n\n");
  if (typeof value !== "object") return String(value);
  return Object.entries(value).map(([key, entry]) => {
    const text = instructionText(entry);
    if (!text) return "";
    const label = key === "TopLevelInstruction" ? "Section instructions"
      : key === "SubSections" ? "Fields" : key.replace(/([a-z])([A-Z])/g, "$1 $2");
    return `${label}\n${text}`;
  }).filter(Boolean).join("\n\n");
}

// Typed visit notes first, then the transcript.
export function transcriptDocuments(detail) {
  return [
    ...(detail.visitNotes ? [{ title: "Typed visit notes", text: detail.visitNotes }] : []),
    { title: "Transcript", text: detail.transcript == null ? "Transcript unavailable for this note." : detail.transcript || "No transcript text was saved." },
  ];
}

// The template's name and its note-wide directive, above the note.
export function TemplateSummary({ instructions }) {
  if (!instructions) return <p className="note-review-label-context">The template for this note is unavailable.</p>;
  return <div className="note-review-template">
    <span>Template <strong>{instructions.name || "Untitled"}</strong></span>
    {instructions.systemDirective && <details className="note-review-system"><summary>Template-wide instructions</summary><div className="note-review-prose">{instructionText(instructions.systemDirective)}</div></details>}
  </div>;
}

const parseJson = (value) => {
  if (typeof value !== "string") return value;
  try { return JSON.parse(value); } catch (_error) { return value; }
};

// A section's instruction is plain text, or {TopLevelInstruction, SubSections}
// where SubSections[name].Instruction is a View More field's instruction. Most
// SubSections entries only carry a replyType, so only filled ones are returned.
export const sectionInstructionParts = (value) => {
  const parsed = parseJson(value);
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return { topLevel: typeof parsed === "string" ? parsed.trim() : "", viewMore: [] };
  }
  const subsections = parseJson(parsed.SubSections);
  const viewMore = Object.entries(subsections && typeof subsections === "object" ? subsections : {})
    .map(([name, subsection]) => ({ name, instruction: String(subsection?.Instruction || "").trim() }))
    .filter((subsection) => subsection.instruction);
  return { topLevel: String(parsed.TopLevelInstruction || "").trim(), viewMore };
};

// The template's instructions for one note section, matched by section name:
// the section's own instruction, then any View More fields that have one.
export function SectionInstructions({ instructions, section }) {
  if (!instructions) return null;
  const entry = Object.entries(instructions.questions || {}).find(([key]) => normalize(key) === normalize(section));
  const { topLevel, viewMore } = sectionInstructionParts(entry?.[1]);
  return <div className="note-review-instructions">
    <h4>Instructions</h4>
    {topLevel && <div className="note-review-prose">{topLevel}</div>}
    {!!viewMore.length && <div className="note-review-view-more">
      <h5>View More</h5>
      {viewMore.map((subsection) => <div key={subsection.name}>
        <strong>{subsection.name}</strong>
        <div className="note-review-prose">{subsection.instruction}</div>
      </div>)}
    </div>}
    {!topLevel && !viewMore.length && <span className="note-accuracy-muted">No instructions in the template for this section.</span>}
  </div>;
}

export function SearchableSource({ documents }) {
  const [query, setQuery] = useState("");
  const [match, setMatch] = useState(0);
  const content = useRef(null);
  const result = useMemo(() => {
    let count = 0;
    const sections = documents.map((document) => {
      const text = document.text || "";
      const parts = [];
      let from = 0;
      let index;
      if (query) {
        const lower = text.toLowerCase();
        while ((index = lower.indexOf(query.toLowerCase(), from)) !== -1) {
          parts.push(text.slice(from, index));
          const position = count++;
          parts.push(<mark key={position} data-match={position} className={position === match ? "note-review-match-current" : ""}>{text.slice(index, index + query.length)}</mark>);
          from = index + query.length;
        }
      }
      parts.push(text.slice(from));
      return { ...document, parts };
    });
    return { sections, count };
  }, [documents, query, match]);
  useEffect(() => {
    content.current?.querySelector(`[data-match="${match}"]`)?.scrollIntoView({ block: "nearest" });
  }, [match, query]);

  return <>
    <div className="note-review-search">
      <label className="sr-only" htmlFor="note-review-source-search">Find in source</label>
      <Input id="note-review-source-search" type="search" value={query} placeholder="Find a phrase or value…" onChange={(event) => { setQuery(event.target.value); setMatch(0); }} />
      {query && <div className="note-review-search-controls">
        <span aria-live="polite">{result.count ? match + 1 : 0} / {result.count}</span>
        <Button size="sm" outline disabled={!result.count} aria-label="Previous match" onClick={() => setMatch((match + result.count - 1) % result.count)}>↑</Button>
        <Button size="sm" outline disabled={!result.count} aria-label="Next match" onClick={() => setMatch((match + 1) % result.count)}>↓</Button>
      </div>}
    </div>
    <div className="note-review-source-content" ref={content}>
      {result.sections.map((document) => <section key={document.title}>
        <h4>{document.title}</h4>
        <div className="note-review-prose">{document.parts}</div>
      </section>)}
    </div>
  </>;
}

// Cora captures checked option labels joined with "$".
function CheckboxSelections({ field, mode }) {
  const before = field.before.split("$").map((choice) => choice.trim()).filter(Boolean);
  const after = field.after.split("$").map((choice) => choice.trim()).filter(Boolean);
  const choices = mode === "ai" ? before : mode === "final" ? after : [...new Set([...before, ...after])];
  if (!choices.length) return <span className="note-accuracy-muted">Empty</span>;

  return <ul className="note-review-selections" aria-label="Checkbox selections">
    {choices.map((choice) => {
      const removed = field.inBaseline && mode === "changes" && !after.includes(choice);
      const added = field.inBaseline && mode === "changes" && !before.includes(choice);
      return <li key={choice}>
        <span aria-hidden="true">{removed ? "☐" : "☑"}</span>
        <span><span className="sr-only">{removed ? "Deselected: " : "Selected: "}</span>{removed ? <del>{choice}</del> : added ? <ins>{choice}</ins> : choice}</span>
      </li>;
    })}
  </ul>;
}

export default function NoteReviewReader({ apiClient, note, index, count, preferredSection, preferredTag, tagLabels, onClose, onNavigate }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const [sectionKey, setSectionKey] = useState("");
  const [mode, setMode] = useState("changes");
  const [showUnchanged, setShowUnchanged] = useState(false);
  const [showSource, setShowSource] = useState(true);
  const [showInstructions, setShowInstructions] = useState(true);
  const documentRef = useRef(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    setDetail(null);
    apiClient.JMPost("/v1/notes/accuracy-tags/detail", { noteId: note.noteId }).then((response) => {
      if (!active) return;
      if (response?.error) throw new Error(response.error);
      setDetail(response);
      const sections = response.sections || [];
      const first = sections.find((section) => section.key === preferredSection)
        || sections.find((section) => preferredTag && section.tags.includes(preferredTag))
        || sections.find((section) => section.fields.some((field) => field.inBaseline && field.changed))
        || sections[0];
      setSectionKey(first?.key || "");
    }).catch(() => {
      if (active) setError("Unable to open this note. Try again or review another note.");
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [apiClient, note.noteId, preferredSection, preferredTag, retry]);

  useEffect(() => { if (documentRef.current) documentRef.current.scrollTop = 0; }, [sectionKey, note.noteId]);
  const sections = detail?.sections || EMPTY;
  const selectedSections = sectionKey ? sections.filter((section) => section.key === sectionKey) : sections;
  const sourceDocuments = useMemo(() => (detail ? transcriptDocuments(detail) : EMPTY), [detail]);

  return <Modal isOpen toggle={onClose} className="note-review-modal" contentClassName="note-review-modal-content" labelledBy="note-review-title" size="xl" returnFocusAfterClose>
    <ModalHeader toggle={onClose} id="note-review-title">
      <span>{note.noteType || "Captured note"}</span>
      <small>{note.userId} · {note.emr || "Unknown EMR"} · Signed <time dateTime={note.signedAt}>{new Date(note.signedAt).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", second: "2-digit", timeZoneName: "short" })}</time></small>
    </ModalHeader>
    <div className="note-review-toolbar">
      <div className="note-review-note-navigation"><Button size="sm" outline disabled={index === 0} onClick={() => onNavigate(index - 1)}>Previous note</Button><span>{index + 1} of {count} on this page</span><Button size="sm" outline disabled={index + 1 === count} onClick={() => onNavigate(index + 1)}>Next note</Button></div>
      <span className="note-review-id">Note {note.noteId.slice(0, 8)} · {Number(note.pctChanged).toFixed(1)}% text change</span>
    </div>
    <ModalBody className="note-review-body">
      {loading ? <div className="note-review-loading"><Spinner /> Opening captured note…</div> : error ? <Alert color="danger">{error} <Button size="sm" onClick={() => setRetry(retry + 1)}>Retry</Button></Alert> : <div className={`note-review-workspace ${showSource ? "" : "note-review-source-hidden"}`}>
        <nav className="note-review-sections" aria-label="Note sections">
          <div className="note-review-section-heading">Sections</div>
          <button className={sectionKey === "" ? "active" : ""} aria-pressed={sectionKey === ""} onClick={() => setSectionKey("")}>All sections</button>
          {sections.map((section) => {
            const changes = section.fields.filter((field) => field.inBaseline && field.changed).length;
            const hasBaseline = section.fields.some((field) => field.inBaseline);
            return <button key={section.key} className={sectionKey === section.key ? "active" : ""} aria-pressed={sectionKey === section.key} onClick={() => setSectionKey(section.key)}>
              <span>{section.key}</span><small>{changes ? `${changes} changed ${changes === 1 ? "field" : "fields"}` : hasBaseline ? "No changes in compared fields" : "No AI baseline captured"}</small>
            </button>;
          })}
        </nav>
        <section className="note-review-document" aria-label="Note comparison">
          <div className="note-review-document-toolbar">
            <div className="note-review-modes" aria-label="Note view">
              {[["changes", "Changes"], ["ai", "AI note"], ["final", "Final note"]].map(([value, label]) => <button key={value} className={mode === value ? "active" : ""} aria-pressed={mode === value} onClick={() => setMode(value)}>{label}</button>)}
            </div>
            <div className="note-review-toggles">
              <Button size="sm" outline aria-pressed={showInstructions} onClick={() => setShowInstructions(!showInstructions)}>{showInstructions ? "Hide instructions" : "Show instructions"}</Button>
              <Button size="sm" outline onClick={() => setShowSource(!showSource)}>{showSource ? "Hide transcript" : "Show transcript"}</Button>
            </div>
          </div>
          <div className="note-review-document-caption">
            {mode === "changes" ? <><span><del>Removed</del> <ins>Added</ins></span><label><input type="checkbox" checked={showUnchanged} onChange={(event) => setShowUnchanged(event.target.checked)} /> Show all captured fields</label></> : <span>{mode === "ai" ? "AI baseline captured in the EMR" : "Captured signed note"}</span>}
          </div>
          <div className="note-review-document-content" ref={documentRef}>
            <p className="note-review-label-context">Only fields with a captured AI baseline can be compared. A missing baseline does not establish who wrote the field. Final note shows all captured fields.</p>
            <TemplateSummary instructions={detail.instructions} />
            {selectedSections.map((section) => {
              const fields = section.fields.filter((field) => mode !== "changes" || showUnchanged || (field.inBaseline && field.changed));
              return <section className="note-review-section" key={section.key}>
                <h3>{section.key}</h3>
                {!!section.tags.length && <><div className="note-accuracy-tags">{section.tags.map((tag) => <span className={`note-accuracy-tag${tag === preferredTag ? " note-accuracy-tag-selected" : ""}`} key={tag}>{tagLabels[tag] || tag}</span>)}</div><p className="note-review-label-context">AI-assigned labels for this section{section.topic ? ` · ${section.topic}` : ""}</p></>}
                {!fields.length && <p className="note-accuracy-muted">{section.fields.some((field) => field.inBaseline) ? "No changes in the compared fields. View Final note to see all captured content." : "No AI baseline was captured for this section. View Final note to see its captured content."}</p>}
                {fields.map((field) => <div className="note-review-field" key={field.name}>
                  {field.name !== section.key && <h4>{field.name}</h4>}
                  {!field.inBaseline && <span className="note-review-label-context">No AI baseline captured for this field; excluded from edit comparison.</span>}
                  <div className="note-review-prose">{!field.inBaseline && mode === "ai" ? <span className="note-accuracy-muted">Baseline unavailable</span>
                    : note.emr === "corahealth.com" && (field.name.endsWith(" - Check Box") || field.name === "Check Box › Check Box")
                    ? <CheckboxSelections field={field} mode={mode} />
                    : mode === "changes" && field.inBaseline ? field.diff.map((piece, pieceIndex) => piece.kind === "delete" ? <del key={pieceIndex}>{piece.text}</del> : piece.kind === "insert" ? <ins key={pieceIndex}>{piece.text}</ins> : <React.Fragment key={pieceIndex}>{piece.text}</React.Fragment>) : (mode === "ai" ? field.before : field.after) || <span className="note-accuracy-muted">Empty</span>}</div>
                </div>)}
                {showInstructions && <SectionInstructions instructions={detail.instructions} section={section.key} />}
              </section>;
            })}
            {!sections.length && <p className="note-accuracy-empty">No captured note text is available.</p>}
          </div>
        </section>
        {showSource && <aside className="note-review-source" aria-label="Transcript">
          <SearchableSource key={note.noteId} documents={sourceDocuments} />
        </aside>}
      </div>}
    </ModalBody>
  </Modal>;
}
